import React, { FC, useEffect, useState, useCallback } from 'react';
import Card from '@salesforce/design-system-react/components/card';
import Input from '@salesforce/design-system-react/components/input';
import Button from '@salesforce/design-system-react/components/button';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import InventoryApproveRejectDataCell from './DataTableCells/InventoryApproveRejectDataCell';
import InventoryCounterDataCell from './DataTableCells/InventoryCounterDataCell';
import InventoryReturnTypeDataCell from './DataTableCells/InventoryReturnTypeDataCell';
import { MAX_QUANTITY } from '../../../util/constants';
import './index.scss';

interface Props {
  inventoryReturnDetails: InventoryReturnDetails;
  handleLineItemActions: (parts: any) => void;
  addLineItems: (parts: any) => void;
  saveLineItems: (parts: any) => void;
  viewOnly: boolean;
}
interface LineItems {
  availableQuantity: string;
  expirationDate: string;
  externalId: string;
  id: string;
  lotNumber: string;
  onhandQuantity: string;
  productDescription: string;
  productId: string;
  productNumber: string;
  quantity: string;
  status: string;
  type: string;
  workflowAction: string;
}

interface InventoryReturnDetails {
  lineItems: LineItems[];
  visualIndicator: string;
  status: string;
}

// CustomDataTableCell must have the same displayName as DataTableCell or it will be ignored.
InventoryApproveRejectDataCell.displayName = DataTableCell.displayName;
InventoryReturnTypeDataCell.displayName = DataTableCell.displayName;
InventoryCounterDataCell.displayName = DataTableCell.displayName;

const PartsInfo: FC<Props> = ({
  inventoryReturnDetails,
  handleLineItemActions,
  addLineItems,
  saveLineItems,
  viewOnly,
}) => {
  const { lineItems } = inventoryReturnDetails;
  const [lineItemsData, setLineItemsData] = useState<LineItems[]>(lineItems);
  useEffect(() => {
    if (lineItemsData) {
      const finalLineItems = lineItems.map((k: LineItems) => {
        const result = lineItemsData.filter(
          (e: LineItems) => e.externalId === k.externalId && e.productId === k.productId
        );
        let type = null;

        if (result && result.length > 0) {
          type = Array.isArray(result[0].type) ? result[0].type[0].value : result[0].type;
        }
        return {
          ...k,
          type,
          userworkflowAction: null,
        };
      });
      setLineItemsData(finalLineItems);
      handleLineItemActions(finalLineItems);
    } else {
      const finalLineItems = lineItems.map((item: LineItems) => {
        return {
          ...item,
          userworkflowAction: null,
        };
      });
      setLineItemsData(finalLineItems);
      handleLineItemActions(finalLineItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- line-item actions are intentionally recalculated only when source data changes
  }, [lineItems, inventoryReturnDetails]);

  const handleCount = useCallback(
    (event: any, data: { value: string; number: number }, product: any): void => {
      const updatedList = lineItemsData.map((element: any) => {
        if (
          product.productId === element.productId &&
          product.lotNumber === element.lotNumber &&
          product.id === element.id
        ) {
          let maxQuantity = element.quantity;
          if (!element.lotNumber) {
            maxQuantity = MAX_QUANTITY;
          } else if (element.availableQuantity > 0) {
            maxQuantity = Number(element.quantity) + Number(element.availableQuantity);
          }

          return {
            ...element,
            maxQuantity,
            counterValue:
              data.number > maxQuantity ? maxQuantity.toString() : data.number.toString(),
            changed: true,
          };
        }
        return element;
      });
      setLineItemsData(updatedList);
      handleLineItemActions(updatedList);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- line-item actions use the latest rendered list intentionally
    [lineItemsData]
  );

  const onSaveClick = () => {
    saveLineItems(lineItemsData);
  };

  return (
    <div className="slds-m-around_small slds-m-bottom_none">
      <Card
        style={{ backgroundColor: '#545857', color: 'white', fontWeight: 600 }}
        id="PartsInfo"
        heading="Inventory"
        headerActions={
          <>
            <Button
              style={{ color: 'black', fontWeight: 600 }}
              iconCategory="utility"
              className="slds-text-color_default"
              iconName="add"
              label="Add"
              iconPosition="left"
              disabled={
                viewOnly ||
                inventoryReturnDetails.status === 'Completed' ||
                inventoryReturnDetails.visualIndicator === 'Completed'
              }
              onClick={addLineItems}
            />
            <Button
              style={{ color: 'black', fontWeight: 600 }}
              className="yellow-btn"
              label="Save"
              iconPosition="left"
              disabled={
                viewOnly ||
                inventoryReturnDetails.status === 'Completed' ||
                inventoryReturnDetails.visualIndicator === 'Completed'
              }
              onClick={onSaveClick}
            />
          </>
        }
      />
      <div style={{ overflow: 'auto' }}>
        <>
          <div className="parts-table-container-IR">
            <div>
              <div className="parts-table-header ir-cols">
                <p>Part #</p>
                <p>Lot</p>
                <p>Part Name</p>
                <p className="pad-left-60">Qty</p>
                {/* <p>Return Type</p> */}
              </div>
              {lineItemsData &&
                lineItemsData.map((lineItem: any) => {
                  const {
                    quantity,
                    availableQuantity,
                    counterValue,
                    workflowAction,
                    lotNumber,
                    productDescription,
                    productNumber,
                  } = lineItem;
                  const count = counterValue || quantity;
                  return (
                    <div>
                      <div>
                        <div className="parts-table-row ir-cols">
                          <p className="vertical-align-middle">{productNumber}</p>
                          <p className="vertical-align-middle">{lotNumber}</p>
                          <p className="vertical-align-middle">{productDescription}</p>
                          <div className="row-control">
                            {lotNumber ? (
                              <Input
                                id="counter-input-3"
                                minValue={0}
                                maxValue={Number(availableQuantity) + Number(quantity)}
                                disabled={
                                  viewOnly ||
                                  !!(
                                    workflowAction &&
                                    (workflowAction.toLowerCase() === 'accept' ||
                                      workflowAction.toLowerCase() === 'cancel')
                                  )
                                }
                                onChange={(
                                  event: any,
                                  data: { value: string; number: number }
                                ): void => {
                                  handleCount(event, data, lineItem);
                                }}
                                value={count}
                                variant="counter"
                              />
                            ) : (
                              <Input
                                id="counter-input-3"
                                minValue={0}
                                maxValue={MAX_QUANTITY}
                                disabled={
                                  viewOnly ||
                                  !!(
                                    workflowAction &&
                                    (workflowAction.toLowerCase() === 'accept' ||
                                      workflowAction.toLowerCase() === 'cancel')
                                  )
                                }
                                onChange={(
                                  event: any,
                                  data: { value: string; number: number }
                                ): void => {
                                  handleCount(event, data, lineItem);
                                }}
                                value={count}
                                variant="counter"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      </div>
    </div>
  );
};

export default PartsInfo;
